import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideLifecycleProps {
  onContinue: () => void;
}
export function SlideLifecycle({
  onContinue
}: SlideLifecycleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 800); // Draw axes + launch line
    const t2 = setTimeout(() => setStage(2), 1800); // Draw main curve
    const t3 = setTimeout(() => setStage(3), 3800); // Show phase labels
    const t4 = setTimeout(() => setStage(4), 5200); // Show potential curve + highlight
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  // SVG coordinates
  // Viewbox: 0 0 1000 480
  // X-axis: y=400, Y-axis: x=80
  // Launch line: y=230 (horizontal dashed)
  //
  // Phases along X:
  // R&D:          x=80  → x=300  (below launch line, slowly rising to cross it)
  // Launch+Mature: x=300 → x=600 (steep rise to peak, then decline)
  // Peak:         ~x=460, y=70
  // Off Patent:   x=600 (patent cliff starts)
  // Legacy tail:  x=680 → x=950 (long gradual decline)
  // Main lifecycle curve path
  const mainCurve = 'M 80 370 C 120 360 180 300 240 260 C 270 240 285 232 300 230 C 330 220 370 140 420 90 C 450 65 480 60 500 65 C 530 75 560 110 600 160 C 630 200 650 280 680 320 C 720 360 800 380 950 390';
  // "Potential" optimized tail curve (diverges from main at patent cliff, stays higher)
  const potentialCurve = 'M 600 160 C 625 190 645 230 680 255 C 720 280 780 295 850 305 C 900 310 930 312 950 315';
  // Area between the two tail curves (the "potential" gap)
  // Goes along potential curve forward, then back along main curve
  const potentialArea = 'M 600 160 C 625 190 645 230 680 255 C 720 280 780 295 850 305 C 900 310 930 312 950 315 L 950 390 C 930 388 800 380 720 360 C 680 340 650 280 630 200 C 620 175 610 165 600 160 Z';
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-103">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-5xl font-serif text-white mb-4" data-id="element-104">
        The Product Lifecycle &{' '}
        <span className="text-teal-400" data-id="element-105">The Untapped Potential</span>
      </motion.h2>

      <div className="flex-1 relative flex items-center justify-center" data-id="element-106">
        {/* Whiteboard reference */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        rotate: -3
      }} animate={{
        opacity: stage >= 4 ? 0.35 : 0,
        scale: 1,
        rotate: -3
      }} transition={{
        duration: 0.8
      }} className="absolute -top-2 right-0 w-44 rounded-lg overflow-hidden border-2 border-white/20 shadow-2xl z-20" data-id="element-107">
          <img src="https://cdn.magicpatterns.com/uploads/myHxqZZxL9vWT45Aao9zJw/Screenshot_2026-05-01_at_11.17.26.png" alt="Original whiteboard sketches" className="w-full h-auto" data-id="element-108" />
          <div className="bg-navy-900/80 text-[10px] text-center py-1 text-white/50" data-id="element-109">
            From whiteboard to strategy
          </div>
        </motion.div>

        <div className="w-full max-w-5xl relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-110">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-111">
            <defs data-id="element-112">
              {/* Hatching pattern for potential area */}
              <pattern id="hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-113">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.3" data-id="element-114" />
              </pattern>
            </defs>

            {/* Y-axis label */}
            <motion.text x="30" y="240" textAnchor="middle" className="fill-white/40 text-xs font-medium" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-115">
              Revenue
            </motion.text>

            {/* X-axis label */}
            <motion.text x="520" y="445" textAnchor="middle" className="fill-white/40 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-116">
              Time
            </motion.text>

            {/* Axes */}
            <motion.line x1="80" y1="400" x2="960" y2="400" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-117" />
            <motion.line x1="80" y1="30" x2="80" y2="420" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-118" />

            {/* Launch Line (horizontal dashed) — must cross this to launch */}
            <motion.line x1="80" y1="230" x2="960" y2="230" stroke="#5EEBD4" strokeWidth="1.5" strokeDasharray="10,6" strokeOpacity="0.5" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} data-id="element-119" />
            <motion.text x="90" y="222" className="fill-teal-300/60 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            delay: 0.8
          }} data-id="element-120">
              Launch threshold
            </motion.text>

            {/* Phase divider lines (vertical dashed) */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.6 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-121">
              {/* R&D → Launch boundary */}
              <line x1="300" y1="40" x2="300" y2="410" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-122" />
              {/* Launch+Mature → Off Patent boundary */}
              <line x1="600" y1="40" x2="600" y2="410" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-123" />
              {/* Off Patent → Legacy boundary */}
              <line x1="680" y1="40" x2="680" y2="410" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-124" />
            </motion.g>

            {/* Phase Labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-125">
              {/* R&D */}
              <text x="190" y="455" textAnchor="middle" className="fill-white/60 text-sm font-medium" data-id="element-126">
                R&D
              </text>
              <text x="190" y="472" textAnchor="middle" className="fill-white/35 text-xs" data-id="element-127">
                8–10 years
              </text>

              {/* Launch + Mature */}
              <text x="450" y="455" textAnchor="middle" className="fill-white/60 text-sm font-medium" data-id="element-128">
                Launch & Mature
              </text>
              <text x="450" y="472" textAnchor="middle" className="fill-white/35 text-xs" data-id="element-129">
                10–12 years
              </text>

              {/* Off Patent */}
              <text x="640" y="455" textAnchor="middle" className="fill-white/50 text-xs font-medium" data-id="element-130">
                Patent
              </text>
              <text x="640" y="468" textAnchor="middle" className="fill-white/50 text-xs font-medium" data-id="element-131">
                Cliff
              </text>

              {/* Foundation / Legacy */}
              <text x="820" y="455" textAnchor="middle" className="fill-teal-400 text-sm font-bold" data-id="element-132">
                Foundation /
              </text>
              <text x="820" y="472" textAnchor="middle" className="fill-teal-400 text-sm font-bold" data-id="element-133">
                Legacy Brands
              </text>
            </motion.g>

            {/* Duration brackets */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.4 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-134">
              {/* R&D bracket */}
              <line x1="90" y1="430" x2="290" y2="430" stroke="#3A5278" strokeWidth="1" data-id="element-135" />
              <line x1="90" y1="425" x2="90" y2="435" stroke="#3A5278" strokeWidth="1" data-id="element-136" />
              <line x1="290" y1="425" x2="290" y2="435" stroke="#3A5278" strokeWidth="1" data-id="element-137" />

              {/* Launch+Mature bracket */}
              <line x1="310" y1="430" x2="590" y2="430" stroke="#3A5278" strokeWidth="1" data-id="element-138" />
              <line x1="310" y1="425" x2="310" y2="435" stroke="#3A5278" strokeWidth="1" data-id="element-139" />
              <line x1="590" y1="425" x2="590" y2="435" stroke="#3A5278" strokeWidth="1" data-id="element-140" />
            </motion.g>

            {/* ===== POTENTIAL AREA (hatched fill between curves) ===== */}
            <motion.path d={potentialArea} fill="url(#hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.2
          }} data-id="element-141" />
            {/* Soft glow fill */}
            <motion.path d={potentialArea} fill="rgba(0, 212, 170, 0.08)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.2
          }} data-id="element-142" />

            {/* ===== MAIN LIFECYCLE CURVE ===== */}
            <motion.path d={mainCurve} fill="none" stroke="#FFFFFF" strokeWidth="3" strokeOpacity="0.8" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.5,
            ease: 'easeInOut'
          }} data-id="element-143" />

            {/* ===== POTENTIAL (OPTIMIZED) TAIL CURVE ===== */}
            <motion.path d={potentialCurve} fill="none" stroke="#00D4AA" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} data-id="element-144" />

            {/* "Potential" label with arrow */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} data-id="element-145">
              <text x="830" y="270" textAnchor="middle" className="fill-teal-400 text-sm font-bold" data-id="element-146">
                POTENTIAL
              </text>
              <text x="830" y="288" textAnchor="middle" className="fill-teal-300/60 text-xs" data-id="element-147">
                Optimization lifts the tail
              </text>
              {/* Small arrow pointing into the gap */}
              <line x1="790" y1="300" x2="790" y2="340" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.6" markerEnd="url(#arrowhead)" data-id="element-148" />
              <line x1="790" y1="370" x2="790" y2="345" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.6" markerEnd="url(#arrowhead)" data-id="element-149" />
            </motion.g>

            {/* Arrow marker */}
            <defs data-id="element-150">
              <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto" data-id="element-151">
                <polygon points="0 0, 6 3, 0 6" fill="#00D4AA" fillOpacity="0.6" data-id="element-152" />
              </marker>
            </defs>

            {/* Peak label */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.4,
            delay: 0.3
          }} data-id="element-153">
              <text x="480" y="50" textAnchor="middle" className="fill-white/50 text-xs font-medium" data-id="element-154">
                Peak Revenue
              </text>
            </motion.g>

            {/* Patent cliff annotation */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.4,
            delay: 0.5
          }} data-id="element-155">
              <text x="635" y="180" textAnchor="middle" className="fill-red-400/60 text-xs font-medium" transform="rotate(55, 635, 180)" data-id="element-156">
                Steep decline
              </text>
            </motion.g>
          </svg>

          {/* Strategic Focus Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 4 ? 1 : 0,
          y: stage >= 4 ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="absolute bottom-2 left-0 bg-navy-700/70 border border-teal-400/30 p-4 rounded-xl max-w-sm backdrop-blur-sm" data-id="element-157">
            <h4 className="text-teal-400 font-semibold mb-1 text-sm" data-id="element-158">
              The Strategic Opportunity
            </h4>
            <p className="text-sm text-white/80 leading-relaxed" data-id="element-159">
              The dashed teal line shows what's possible with continuous
              optimization — slowing the decline and extending the revenue tail
              by even a few percentage points.
            </p>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-160">
        <Button onClick={onContinue} data-id="element-161">See the Revenue Reality</Button>
      </motion.div>
    </div>;
}